import { Box, Button, Flex, Icon, Text, Textarea, VStack } from '@chakra-ui/react';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, format, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import PrimaryButton from '../../../../../../../components/ui/buttons/PrimaryButton';
import { getApiErrorMessage } from '../../../../../../../services/marketplace/errors';
import { getLeadByExternalIdQueryKey } from '../../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePostLeadByExternalIdPickupAvailability } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdPickupAvailability';
import { convertTimeZone } from '../../../../../../../utils/dates';
import OfferActionModal from '../../offers/modals/OfferActionModal';
import { PICKUP_WINDOWS, PickupWindowKey, formatPickupDaySummary } from '../utils';

const MIN_DAYS = 2;
const MAX_DAYS = 4;
const FIRST_DAY_OFFSET = 2;
const LAST_DAY_OFFSET = 14;
const COLLAPSED_ROWS = 7;
const NOTE_MAX = 200;

const timezoneShortLabel = (timeZone: string): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' })
      .formatToParts(new Date())
      .find((formatted) => formatted.type === 'timeZoneName');
    return part?.value ?? timeZone;
  } catch {
    return timeZone;
  }
};

const statusLineFor = (count: number): string => {
  if (count === 0) return `Pick at least ${MIN_DAYS} days`;
  if (count === 1) return '1 day picked. Pick 1 more';
  if (count === 2) return '2 days picked. A third gives them a backup';
  if (count >= MAX_DAYS) return `${count} days picked, the max`;
  return `${count} days picked`;
};

type PickupAvailabilityModalProps = {
  isOpen: boolean;
  onClose: () => void;
  externalId: string;
  pickupTimezone: string;
  dealershipName: string;
};

const PickupAvailabilityModal = ({
  isOpen,
  onClose,
  externalId,
  pickupTimezone,
  dealershipName,
}: PickupAvailabilityModalProps) => {
  const [view, setView] = useState<'picker' | 'success'>('picker');
  const [picks, setPicks] = useState<Record<string, PickupWindowKey[]>>({});
  const [note, setNote] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: shareAvailability, isPending } = usePostLeadByExternalIdPickupAvailability();

  useEffect(() => {
    if (!isOpen) return;
    setView('picker');
    setPicks({});
    setNote('');
    setExpanded(false);
    setSaveError(null);
  }, [isOpen]);

  const days = useMemo(() => {
    const todayAtPickup = startOfDay(convertTimeZone(new Date(), pickupTimezone));
    return Array.from({ length: LAST_DAY_OFFSET - FIRST_DAY_OFFSET + 1 }, (_, index) =>
      addDays(todayAtPickup, FIRST_DAY_OFFSET + index),
    );
  }, [isOpen, pickupTimezone]);

  const pickedDays = Object.keys(picks)
    .filter((day) => picks[day].length > 0)
    .sort();
  const count = pickedDays.length;
  const atMax = count >= MAX_DAYS;
  const canShare = count >= MIN_DAYS;
  const trimmedNote = note.trim();

  const toggleWindow = (day: string, window: PickupWindowKey) => {
    setPicks((current) => {
      const active = current[day] ?? [];
      if (active.includes(window)) {
        return { ...current, [day]: active.filter((key) => key !== window) };
      }
      if (atMax && active.length === 0) return current;
      return {
        ...current,
        [day]: PICKUP_WINDOWS.map((option) => option.key).filter(
          (key) => key === window || active.includes(key),
        ),
      };
    });
  };

  const submit = async () => {
    setSaveError(null);
    const dates = pickedDays.map((day) => ({ date: day, windows: picks[day] }));
    try {
      await shareAvailability({
        externalId,
        data: { dates, note: trimmedNote || undefined },
      });
      queryClient.invalidateQueries({ queryKey: getLeadByExternalIdQueryKey({ externalId }) });
      setView('success');
    } catch (error) {
      setSaveError(getApiErrorMessage(error, "We couldn't send that. Please try again."));
    }
  };

  const shownDays = expanded ? days : days.slice(0, COLLAPSED_ROWS);

  if (view === 'success') {
    return (
      <OfferActionModal isOpen={isOpen} onClose={onClose} title="Pickup availability">
        <VStack spacing="12px" px="24px" pt="8px" pb="24px" textAlign="center">
          <Flex
            w="52px"
            h="52px"
            borderRadius="full"
            align="center"
            justify="center"
            bg="leaseEndGreen"
            color="white"
          >
            <Icon as={FiCheck} boxSize="26px" />
          </Flex>
          <Text fontFamily="heading" fontSize="20px" fontWeight={700} color="leaseEndBlue">
            Availability shared
          </Text>
          <Text fontSize="14px" color="grayMedium">
            We sent this to {dealershipName}. They'll reach out to lock one in.
          </Text>
          <VStack spacing="4px" pt="4px">
            {pickedDays.map((day) => (
              <Text key={day} fontSize="14px" fontWeight={600} color="charcoal">
                {formatPickupDaySummary(day, picks[day])}
              </Text>
            ))}
            {trimmedNote && (
              <Text fontSize="13px" color="grayMedium" fontStyle="italic">
                "{trimmedNote}"
              </Text>
            )}
          </VStack>
          <PrimaryButton w="100%" mt="8px" onClick={onClose}>
            DONE
          </PrimaryButton>
        </VStack>
      </OfferActionModal>
    );
  }

  return (
    <OfferActionModal isOpen={isOpen} onClose={onClose} title="Pickup availability">
      <Box>
        <Box px="24px">
          <Text fontSize="14px" color="grayMedium">
            Pick the times that work for you. {dealershipName} will reach out to lock one in.
          </Text>

          {atMax && (
            <Box mt="12px" p="10px 12px" borderRadius="10px" bg="grayLightest" fontSize="13px">
              <Text as="span" fontWeight={700}>
                That's {MAX_DAYS} days, the most you can pick.
              </Text>{' '}
              Unpick one to swap it. You can still add times to a day you picked.
            </Box>
          )}
        </Box>

        <Flex direction="column" gap="8px" maxH="40dvh" overflowY="auto" px="24px" py="12px">
          {shownDays.map((day, index) => {
            const dayKey = format(day, 'yyyy-MM-dd');
            const activeWindows = picks[dayKey] ?? [];
            const isPicked = activeWindows.length > 0;
            const isLocked = atMax && !isPicked;
            const showMonthLabel =
              index === 0 || day.getMonth() !== shownDays[index - 1].getMonth();
            return (
              <Box key={dayKey}>
                {showMonthLabel && (
                  <Flex
                    align="baseline"
                    justify="space-between"
                    mt={index === 0 ? 0 : '8px'}
                    mb="6px"
                  >
                    <Text
                      fontSize="12px"
                      fontWeight={700}
                      color="grayMedium"
                      textTransform="uppercase"
                    >
                      {format(day, 'MMMM')}
                    </Text>
                    {index === 0 && (
                      <Text fontSize="11px" color="grayMedium">
                        Times in {timezoneShortLabel(pickupTimezone)}, from your pickup address
                      </Text>
                    )}
                  </Flex>
                )}
                <Flex
                  align="center"
                  gap="10px"
                  p="8px 10px"
                  borderRadius="12px"
                  borderWidth="1px"
                  borderColor={isPicked ? 'leaseEndBlue' : 'platinum'}
                  bg={isPicked ? 'blue.50' : 'white'}
                  opacity={isLocked ? 0.45 : 1}
                >
                  <Box flex="none" w="64px">
                    <Text fontSize="14px" fontWeight={700} color="charcoal">
                      {format(day, 'EEE')}
                    </Text>
                    <Text fontSize="12px" color="grayMedium">
                      {format(day, 'MMM d')}
                    </Text>
                  </Box>
                  <Flex flex="1" minW={0} gap="6px">
                    {PICKUP_WINDOWS.map((window) => {
                      const isOn = activeWindows.includes(window.key);
                      return (
                        <Button
                          key={window.key}
                          aria-label={`${window.label}, ${window.range}, ${format(
                            day,
                            'EEE MMM d',
                          )}`}
                          aria-pressed={isOn}
                          onClick={() => toggleWindow(dayKey, window.key)}
                          isDisabled={isLocked}
                          size="sm"
                          flex="1"
                          minW={0}
                          h="auto"
                          px="4px"
                          py="6px"
                          flexDirection="column"
                          variant="outline"
                          borderColor={isOn ? 'leaseEndBlue' : 'platinum'}
                          bg={isOn ? 'leaseEndBlue' : 'white'}
                          color={isOn ? 'white' : 'charcoal'}
                          _hover={isLocked ? undefined : { borderColor: 'leaseEndBlue' }}
                        >
                          <Text fontSize="12px" fontWeight={700}>
                            {window.label}
                          </Text>
                          <Text fontSize="10px" fontWeight={400} opacity={0.8}>
                            {window.range}
                          </Text>
                        </Button>
                      );
                    })}
                  </Flex>
                </Flex>
              </Box>
            );
          })}
          {!expanded && (
            <Button
              variant="ghost"
              size="sm"
              w="100%"
              color="leaseEndBlue"
              onClick={() => setExpanded(true)}
            >
              Show {days.length - COLLAPSED_ROWS} more days
            </Button>
          )}
        </Flex>

        <Box px="24px" pt="12px" pb="24px" borderTopWidth="1px" borderColor="platinum">
          <Flex justify="space-between" align="baseline">
            <Text fontSize="13px" fontWeight={600} color="charcoal">
              Anything {dealershipName} should know?
            </Text>
            <Text fontSize="11px" color="grayMedium">
              optional · {note.length}/{NOTE_MAX}
            </Text>
          </Flex>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
            placeholder="Example: I can do 6 to 9 on weeknights, not 5 to 8."
            fontSize="13px"
            rows={2}
            mt="6px"
            resize="none"
          />
          <Text
            fontSize="12px"
            fontWeight={600}
            color={canShare ? 'leaseEndGreen' : 'grayMedium'}
            mt="8px"
          >
            {statusLineFor(count)}
          </Text>
          {saveError && (
            <Text fontSize="12px" color="leaseEndRed" mt="4px">
              {saveError}
            </Text>
          )}
          <PrimaryButton
            w="100%"
            mt="8px"
            mb="4px"
            isDisabled={!canShare || isPending}
            loading={isPending}
            onClick={submit}
          >
            SHARE MY AVAILABILITY
          </PrimaryButton>
        </Box>
      </Box>
    </OfferActionModal>
  );
};

export default PickupAvailabilityModal;
